// ---------------------------------------------------------------------------
// Buttons.
//
// Sizes are tap-target driven, not type driven: `sm` is already 44px because it
// is still something a thumb has to hit, and `xl` is the 88px vowel/consonant
// button that gets pressed nine times against a thirty second budget.
//
// A disabled button becomes a flat surface with dim text rather than a faded
// primary, because a dimmed blue gradient still reads as the thing to press.
//
// Two rules that are structural rather than decorative:
//
//   1. A label never wraps. `whitespace-nowrap` is on the base class, so a long
//      label runs out of the button rather than turning it into two stacked
//      lines that shove the row below it down. Anything that needs a second
//      piece of information carries it as a badge beside the label.
//   2. `loading` keeps the label in the DOM at full width and lays a spinner
//      over it. Swapping the text for a spinner, or for a different sentence,
//      resizes the button mid-press and moves whatever sits next to it.
// ---------------------------------------------------------------------------

import type { ButtonHTMLAttributes, ReactElement } from 'react';

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'good' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Renders as a spinner-free disabled surface plus dim text. */
  disabled?: boolean;
  /**
   * Work is in flight. The button stops accepting presses and shows a spinner
   * over its own label, so its width never changes and it keeps its
   * accessible name.
   */
  loading?: boolean;
  block?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'btn-primary-surface hover:brightness-110 active:brightness-95',
  outline: 'border border-line bg-transparent text-ink hover:border-line-strong hover:bg-surface-2',
  ghost: 'bg-transparent text-ink-muted hover:bg-surface-2 hover:text-ink',
  good: 'border border-good/60 bg-transparent text-good hover:border-good',
  danger: 'border border-alert/60 bg-transparent text-alert hover:border-alert',
};

const SIZES = {
  sm: 'h-11 rounded-[9px] px-3 text-[13px] font-semibold',
  md: 'h-12 rounded-[11px] px-4 text-[15px] font-bold',
  lg: 'h-[52px] rounded-[11px] px-5 text-[16px] font-bold',
  xl: 'h-[88px] rounded-frame px-6 text-[20px] font-bold',
} as const;

const SPINNER_SIZE = {
  sm: 'size-4',
  md: 'size-[18px]',
  lg: 'size-5',
  xl: 'size-7',
} as const;

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  loading = false,
  className,
  children,
  ...rest
}: ButtonProps): ReactElement {
  // A button that is working is not a button that is off: it keeps its own
  // surface so the press the player just made still looks like it landed.
  const inert = disabled && !loading;

  return (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'relative inline-flex shrink-0 items-center justify-center gap-2 text-center whitespace-nowrap',
        'transition-[background-color,border-color,color,filter] duration-150 ease-out',
        SIZES[size],
        inert ? 'cursor-not-allowed bg-raised text-ink-dim' : VARIANTS[variant],
        loading ? 'cursor-progress' : '',
        block ? 'w-full' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {/* Faded rather than hidden: `visibility: hidden` would take the label out
          of the accessibility tree and leave a button with no name. */}
      <span
        className={`inline-flex items-center justify-center gap-2 transition-opacity duration-150 ${
          loading ? 'opacity-0' : ''
        }`}
      >
        {children}
      </span>
      {loading && (
        <span className="absolute inset-0 grid place-items-center">
          <span
            aria-hidden="true"
            // `busy-spinner`, not Tailwind's `animate-spin`. The reduced-motion
            // block in index.css crushes every animation on `*` to a hundredth
            // of a millisecond, which froze this ring solid for anybody with
            // the OS preference on — a still wheel over a label saying work is
            // in flight. That rule owns the exemption and the reduced-motion
            // treatment together; read the comment above the keyframes there.
            className={`${SPINNER_SIZE[size]} busy-spinner rounded-full border-2 border-current border-t-transparent`}
          />
        </span>
      )}
    </button>
  );
}

export interface IconButtonProps extends ButtonProps {
  /** Required: an icon alone has no accessible name. */
  label: string;
}

export function IconButton({ label, size = 'md', className, ...rest }: IconButtonProps): ReactElement {
  const square = size === 'sm' ? 'w-11' : size === 'md' ? 'w-12' : size === 'lg' ? 'w-[52px]' : 'w-[88px]';
  return (
    <Button
      aria-label={label}
      size={size}
      className={`${square} px-0 ${className ?? ''}`}
      {...rest}
    />
  );
}
